import { useMemo } from 'react';
import { createHook, createSelector, type BoundActions } from '@atlassian/react-sweet-state';
import type { State, ThirdPartyEntityStatusesState } from '../../../../common/types';
import { useStatusMappingEntityContext } from '../../../../common/utils/status-mapping-entity-context';
import type { Actions } from '../../actions';
import type { SetThirdPartyEntityStatusMappingAction } from '../../actions/set-third-party-status-mapping';
import { Store } from '../../main';
import { useThirdPartyState } from '../third-party-state';

const thirdPartyStatusMappingSelector = (state: State): ThirdPartyEntityStatusesState | null =>
	state.thirdPartyState.thirdPartyStatusMapping;

export type ThirdPartyStatusMappingActions = {
	setThirdPartyStatusMapping: SetThirdPartyEntityStatusMappingAction;
};

export type UseThirdPartyStatusMappingState = () => [
	ThirdPartyEntityStatusesState | null,
	BoundActions<State, ThirdPartyStatusMappingActions>,
];

export const useThirdPartyStatusMappingState: UseThirdPartyStatusMappingState = () => {
	const [state, { setThirdPartyStatusMapping }] = useThirdPartyState();
	const currentEntity = useStatusMappingEntityContext();

	const actions = useMemo(
		() => ({
			setThirdPartyStatusMapping: setThirdPartyStatusMapping.bind(null, currentEntity),
		}),
		[currentEntity, setThirdPartyStatusMapping],
	);

	return [state.thirdPartyStatusMapping, actions];
};

export type ThirdPartyMappedStatus = {
	numTotalStatuses: number;
	numMappedStatuses: number;
};

export type UseThirdPartyMappedStatus = () => [
	ThirdPartyMappedStatus,
	Partial<BoundActions<State, Actions>>,
];

export const thirdPartyMappedStatusSelector = createSelector(
	thirdPartyStatusMappingSelector,
	(newState: ThirdPartyEntityStatusesState | null) => {
		let numTotalStatuses = 0;
		let numMappedStatuses = 0;
		if (newState == null) return { numTotalStatuses, numMappedStatuses };

		const { entityStatusMappings } = newState;

		Object.values(entityStatusMappings).forEach((entity) => {
			numTotalStatuses += Object.values(entity.statusMappings).length;
			numMappedStatuses += Object.values(entity.statusMappings).filter(
				(status) => status.mappedStatusName !== null,
			).length;
		});
		return { numTotalStatuses, numMappedStatuses };
	},
);

export const useThirdPartyMappedStatus = createHook(Store, {
	selector: thirdPartyMappedStatusSelector,
});
export type ThirdPartyStatusCategories = {
	statusCategories: string[];
};

export type UseThirdPartyStatusCategories = () => [
	ThirdPartyStatusCategories,
	Partial<BoundActions<State, Actions>>,
];

export const thirdPartyStatusCategorySelector = createSelector(
	thirdPartyStatusMappingSelector,
	(newState: ThirdPartyEntityStatusesState | null) => {
		if (newState == null) return { statusCategories: [] };
		const { statusOptions } = newState;
		return {
			statusCategories: [...new Set(statusOptions.map((status) => status.statusCategory))],
		};
	},
);

export const useThirdPartyStatusCategories = createHook(Store, {
	selector: thirdPartyStatusCategorySelector,
});
